'use client';

import { ChevronDown } from 'lucide-react';
import { Link } from 'react-router';
import { useCallback, useRef, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { FOOTER_ITEM_TRACKING, footerTracking, LEGAL_MENU, RESOURCES_MENU, type FooterMenu, type FooterMenuItem, type FooterMenuLabels } from './footerNavigation';

const menuItemClassName = cn(
    'block cursor-pointer rounded-[36px] py-2 pr-4 pl-6',
    'text-[16px] leading-6 font-bold',
    'light:text-gz-neutral-900 dark:text-gz-neutral-100',
    'light:hover:bg-gz-tan-350 dark:hover:bg-gz-blue-600',
    'focus-visible:outline-2 focus-visible:outline-offset-0',
    'light:focus-visible:outline-gz-marine-700 dark:focus-visible:outline-gz-marine-500',
);

interface FooterMenuEntryProps {
    item: FooterMenuItem;
    label: string;
    /** `navigated` is true when picking this entry left the page. */
    onDone: (navigated: boolean) => void;
    hrefs?: Record<string, string>;
    reloadLinks?: boolean;
    loggedIn: boolean;
}

/** Most entries navigate; "Cookie settings" opens the Osano drawer instead. */
const FooterMenuEntry = ({ item, label, onDone, hrefs, reloadLinks, loggedIn }: FooterMenuEntryProps) => {
    const tracking = footerTracking(FOOTER_ITEM_TRACKING[item.id].click, FOOTER_ITEM_TRACKING[item.id].buttonName, loggedIn);

    if ('to' in item) {
        return (
            <Link
                to={hrefs?.[item.to] ?? item.to}
                reloadDocument={reloadLinks}
                onClick={() => onDone(true)}
                className={menuItemClassName}
                {...tracking}
            >
                {label}
            </Link>
        );
    }

    return (
        <button
            type="button"
            onClick={() => {
                item.onSelect();
                onDone(false);
            }}
            className={cn(menuItemClassName, 'w-full border-0 bg-transparent text-left')}
            {...tracking}
        >
            {label}
        </button>
    );
};

interface FooterDropdownProps {
    menu: FooterMenu;
    /** Translated by the caller (see footerStrings.ts). */
    labels: FooterMenuLabels;
    /** Link targets for the page's language, by default path (see FooterBodyProps.hrefs). */
    hrefs?: Record<string, string>;
    /** Full page loads instead of in-app routing (see FooterBodyProps.reloadLinks). */
    reloadLinks?: boolean;
    loggedIn: boolean;
}

/**
 * Footer menu of navigation links. Built on the shared Popover so Escape,
 * click-outside, focus return and `aria-expanded`/`aria-controls` come from the
 * primitive. `side="top"` opens it upward — the footer sits at the page bottom —
 * and Radix's collision handling keeps the panel on screen on narrow viewports.
 */
export const FooterDropdown = ({ menu, labels, hrefs, reloadLinks, loggedIn }: FooterDropdownProps) => {
    const [open, setOpen] = useState(false);

    // Radix returns focus to the trigger when the panel closes, which is right
    // for Escape but fights `ScrollToTop` after navigation — focusing the
    // trigger scrolls the footer back into view. Suppress it in that one case.
    const shouldRestoreFocus = useRef(true);

    const close = useCallback((navigated: boolean) => {
        shouldRestoreFocus.current = !navigated;
        setOpen(false);
    }, []);

    const onCloseAutoFocus = useCallback((event: Event) => {
        if (!shouldRestoreFocus.current) {
            event.preventDefault();
        }
        shouldRestoreFocus.current = true;
    }, []);

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger
                className={cn(
                    'flex cursor-pointer items-center gap-1 rounded-[38px] border-0 px-4 py-1',
                    'bg-transparent text-[18px] leading-6 font-bold',
                    'light:text-gz-neutral-900 dark:text-gz-neutral-100',
                    'light:hover:bg-gz-tan-350 dark:hover:bg-gz-blue-700',
                    'focus-visible:outline-2 focus-visible:outline-offset-0',
                    'light:focus-visible:outline-gz-marine-700 dark:focus-visible:outline-gz-marine-500',
                    'light:data-[state=open]:bg-gz-tan-350 dark:data-[state=open]:bg-gz-blue-700',
                )}
            >
                {labels.menu}
                <ChevronDown
                    aria-hidden="true"
                    className={cn('size-6 shrink-0 transition-transform', open && 'rotate-180')}
                />
            </PopoverTrigger>
            <PopoverContent
                side="top"
                align="start"
                alignOffset={-5}
                sideOffset={8}
                aria-label={labels.menu}
                onCloseAutoFocus={onCloseAutoFocus}
                className={cn(
                    'w-max min-w-[235px] rounded-[26px] p-4',
                    'light:bg-gz-surface-light light:shadow-[0_2px_4px_0_rgba(35,33,31,0.2)]',
                    'dark:bg-gz-blue-700 dark:shadow-[0_2px_4px_0_rgba(14,13,12,0.2)]',
                )}
            >
                <ul className="flex flex-col gap-2">
                    {menu.items.map(item => (
                        <li key={item.id} className="w-full">
                            <FooterMenuEntry item={item} label={labels.items[item.id]} onDone={close} hrefs={hrefs} reloadLinks={reloadLinks} loggedIn={loggedIn} />
                        </li>
                    ))}
                </ul>
            </PopoverContent>
        </Popover>
    );
};

const MENUS = { resources: RESOURCES_MENU, legal: LEGAL_MENU } as const;

/**
 * A footer menu by name. The legal menu carries a function (the cookie drawer), which cannot be
 * passed from a server component to a client one, so the client side picks the menu itself. Its
 * labels are plain strings, so the server can translate them.
 */
export const FooterMenuDropdown = ({ menu, labels, hrefs, reloadLinks, loggedIn }: { menu: keyof typeof MENUS; labels: FooterMenuLabels; hrefs?: Record<string, string>; reloadLinks?: boolean; loggedIn: boolean }) => (
    <FooterDropdown menu={MENUS[menu]} labels={labels} hrefs={hrefs} reloadLinks={reloadLinks} loggedIn={loggedIn} />
);
